/**
 * School Segmentation Entity Schema
 *
 * The closed, PII-free per-school segmentation bundle (Initiative B, U1 /
 * KTD5). Composed at read time from existing services — it does NOT mirror a
 * `School.tier` column (tier is a derived view, D5). The first consumer is the
 * sync circuit-breaker's R7 autonomy gate (Initiative A).
 *
 * CONTRACT INVARIANTS (the reason this schema is `.strict()`):
 * - **Closed shape, no PII** — exactly the fields below, no open-ended bag,
 *   no person/parent/student names, emails, or free-form notes. Every nested
 *   object is `.strict()` so an unknown or PII-shaped key is REJECTED at parse
 *   time, not silently passed through.
 * - **`unknown` is first-class** on both `tier` and `priority` — it is the
 *   fail-safe floor R7 keys off (KTD4): a missing tier row, a fail-open
 *   `healthRead: 'assumed'`, or any unresolvable input must surface as
 *   `unknown` so R7 never auto-acts on an unverifiable school.
 * - **`tier` is lowercased** (mirrors `SubscriptionStatus`) plus `unknown`.
 * - **`healthRead` distinguishes verified from assumed** — `integration-health`
 *   fails OPEN (`status: HEALTHY, reason: 'health-read-error'`), so a bare
 *   `HEALTHY` can mean "verified healthy" OR "couldn't tell". `healthRead`
 *   makes that explicit (`verified` vs `assumed`).
 */

import { z } from 'zod'

/**
 * Derived tier. The five lowercased `SubscriptionStatus` values plus the
 * first-class `unknown` fail-safe (no resolvable primary subscription row).
 */
export const SchoolTierSchema = z.enum(['trial', 'paying', 'churned', 'expired', 'cancelled', 'unknown'])
export type SchoolTier = z.infer<typeof SchoolTierSchema>

/**
 * Derived priority for the consumer (R7). `unknown` is the UNCONDITIONAL
 * fail-safe floor — any unverifiable input collapses here so R7 must NOT
 * auto-act.
 */
export const SchoolPrioritySchema = z.enum(['low', 'medium', 'high', 'unknown'])
export type SchoolPriority = z.infer<typeof SchoolPrioritySchema>

/**
 * Integration (Blackbaud data-sync) health, derived from
 * `getSchoolDataIntegrationHealth`. `healthRead` carries the verified-vs-
 * assumed distinction the fail-open `integration-health` reader would
 * otherwise hide.
 */
export const SchoolSegmentationIntegrationSchema = z
  .object({
    status: z
      .enum(['HEALTHY', 'DOWN'])
      .describe('Deterministic data-sync health derived from the DB (no live BB call)'),
    reason: z.string().describe('PII-free reason code, e.g. no-pointer-token, revoked, health-read-error'),
    livePeerAvailable: z.boolean().describe('Whether a live-token peer exists for the school'),
    lastSuccessfulSyncAt: z
      .string()
      .nullable()
      .describe('ISO timestamp of the last successful sync (job-derived), or null if never'),
    healthRead: z
      .enum(['verified', 'assumed'])
      .describe('verified = read succeeded; assumed = fail-open (health-read-error), treat HEALTHY as unproven')
  })
  .strict()
export type SchoolSegmentationIntegration = z.infer<typeof SchoolSegmentationIntegrationSchema>

/**
 * The "live token but the sync isn't actually working" proxy (SCH-451
 * signature). `value` combines a non-token-absent failure counter with a
 * never/stale-synced-while-HEALTHY signal; `failingSyncTypeCount` is the
 * number of enabled `(school, sync_type)` pairs with a non-zero failure
 * counter.
 */
export const SchoolSegmentationSyncFailingSchema = z
  .object({
    value: z.boolean().describe('True when the school has a live integration that is not actually syncing'),
    failingSyncTypeCount: z
      .number()
      .int()
      .min(0)
      .describe('Count of enabled (school, sync_type) pairs with consecutiveFailures > 0')
  })
  .strict()
export type SchoolSegmentationSyncFailing = z.infer<typeof SchoolSegmentationSyncFailingSchema>

/**
 * The closed, PII-free per-school segmentation bundle. `.strict()` — an extra
 * or PII-shaped top-level key is rejected at parse time.
 */
export const SchoolSegmentationSchema = z
  .object({
    tier: SchoolTierSchema.describe('Derived tier (lowercased subscription status, or unknown)'),
    priority: SchoolPrioritySchema.describe('Derived priority with an unknown fail-safe floor (KTD4)'),
    atRisk: z
      .boolean()
      .describe('Whether a paying school is at risk (DOWN or syncFailing); only meaningful for paying'),
    integration: SchoolSegmentationIntegrationSchema.describe('Derived data-sync integration health'),
    syncFailing: SchoolSegmentationSyncFailingSchema.describe('Live-token-but-not-working proxy')
  })
  .strict()
export type SchoolSegmentation = z.infer<typeof SchoolSegmentationSchema>
